import { fixtureCreatedClient } from '@/features/clients/infrastructure/fixtures/FixtureClientRepository'
import type { ContractRepository } from '../../application/ContractRepository'
import {
  contractMatchesDisplayStatus,
  deriveContractDisplayStatus,
  isVipContract,
  hasContractPeriod,
  type ContractDataSource,
  type ContractRecord,
  type ContractScheduleEntry,
  type ContractServiceLocation,
  type ContractStats,
} from '../../domain/contract'
import { contractsFixtures } from './contractsFixtures'

/**
 * Brouillons composés pendant la session, jamais persistés.
 *
 * Les fixtures refusent d'inventer une donnée contractuelle — une zone, un
 * échéancier ou un envoi simulés donneraient une fausse idée de ce qui est dû.
 * Rejouer ce que l'opérateur vient de saisir est autre chose : rien n'est
 * inventé, et c'est ce qui rend l'Assistant Nouvelle soumission parcourable en
 * entier sans Supabase, jusqu'au document produit.
 */
const draftLocations = new Map<string, ContractServiceLocation[]>()
const draftSchedules = new Map<string, ContractScheduleEntry[]>()

export function createFixtureContractRepository(source: ContractDataSource = 'fixtures'): ContractRepository {
  const records = [...contractsFixtures]
  return {
    list(filters) {
      const search = filters.search?.trim().toLocaleLowerCase('fr-CA')
      const filtered = records.filter((contract) => {
        if (filters.status && contract.status !== filters.status) return false
        if (filters.displayStatus && !contractMatchesDisplayStatus(contract, filters.displayStatus))
          return false
        if (filters.clientId && contract.clientId !== filters.clientId) return false
        if (filters.vip && !isVipContract(contract)) return false
        if (!search) return true
        return [
          contract.number,
          contract.season,
          contract.notes,
          contract.clientDisplayName,
          contract.clientPhone,
          contract.clientEmail,
          contract.billingAddress?.line1,
          contract.billingAddress?.city,
          contract.billingAddress?.postalCode,
        ]
          .filter(Boolean)
          .some((value) => value?.toLocaleLowerCase('fr-CA').includes(search))
      })
      const { sort } = filters
      if (sort) {
        const direction = sort.direction === 'asc' ? 1 : -1
        const value = (contract: (typeof records)[number]): string | number =>
          ({
            number: contract.number,
            client: contract.clientDisplayName,
            status: deriveContractDisplayStatus(contract),
            price: contract.priceCents ?? -1,
          })[sort.key]
        filtered.sort((left, right) => {
          const a = value(left)
          const b = value(right)
          return (
            (typeof a === 'number' && typeof b === 'number'
              ? a - b
              : String(a).localeCompare(String(b), 'fr-CA')) * direction
          )
        })
      }
      const start = (filters.page - 1) * filters.pageSize
      const count = (status: (typeof contractsFixtures)[number]['status']) =>
        records.filter((contract) => contract.status === status).length
      const countDerived = (status: NonNullable<typeof filters.displayStatus>) =>
        records.filter((contract) => contractMatchesDisplayStatus(contract, status)).length
      const stats: ContractStats = {
        total: records.length,
        draft: countDerived('DRAFT'),
        pending: count('PENDING'),
        active: count('ACTIVE'),
        suspended: countDerived('SUSPENDED'),
        expired: countDerived('EXPIRED'),
        cancelled: countDerived('CANCELLED'),
        awaitingSignature: countDerived('AWAITING_SIGNATURE'),
        awaitingSignatureOpened: countDerived('AWAITING_SIGNATURE_OPENED'),
        awaitingPayment: countDerived('AWAITING_PAYMENT'),
        activeSigned: countDerived('ACTIVE'),
        notInterested: countDerived('NOT_INTERESTED'),
        vip: records.filter((contract) => isVipContract(contract)).length,
      }
      return Promise.resolve({
        items: filtered.slice(start, start + filters.pageSize),
        page: filters.page,
        pageSize: filters.pageSize,
        total: filtered.length,
        stats,
        meta: { source, retrievedAt: new Date().toISOString(), anomalies: 0 },
      })
    },
    getByReference(reference) {
      const contract = records.find((item) => item.id === reference || item.number === reference)
      return Promise.resolve(
        contract
          ? {
              contract,
              meta: {
                source,
                retrievedAt: new Date().toISOString(),
                anomalies: contract.status === 'ACTIVE' && !hasContractPeriod(contract) ? 1 : 0,
              },
            }
          : null,
      )
    },
    createFromQuote() {
      return Promise.reject(new Error('CONTRACT_CREATE_SUPABASE_REQUIRED'))
    },
    listZones() {
      // Aucune zone n'est simulée : une mesure inventée fausserait une surface
      // qui sert à tarifer.
      return Promise.resolve([])
    },
    measurementState() {
      return Promise.resolve({ revision: 0, sumSurfaceM2: 0, overlapM2: 0, excludedM2: 0, netSurfaceM2: 0 })
    },
    replaceZones() {
      return Promise.reject(new Error('CONTRACT_ZONES_SUPABASE_REQUIRED'))
    },
    attachMeasurementPlan() {
      return Promise.reject(new Error('CONTRACT_ZONES_SUPABASE_REQUIRED'))
    },
    createDraft(command) {
      const contract = fixtureCreatedClient(command.clientId)
      const id = crypto.randomUUID()
      const now = new Date().toISOString()
      const record: ContractRecord = {
        id,
        number: `CTR-9${String(records.length + 1).padStart(5, '0')}`,
        status: 'DRAFT',
        clientId: command.clientId,
        clientDisplayName: contract?.displayName ?? 'Client',
        ...(contract?.phone ? { clientPhone: contract.phone } : {}),
        ...(contract?.mobilePhone ? { clientMobilePhone: contract.mobilePhone } : {}),
        ...(contract?.email ? { clientEmail: contract.email } : {}),
        season: command.season,
        renewal: false,
        measurementMode: command.measurementMode ?? 'NONE',
        ...(command.standardDrivewayCapacity === undefined
          ? {}
          : { standardDrivewayCapacity: command.standardDrivewayCapacity }),
        ...(command.serviceAddress ? { serviceAddress: command.serviceAddress } : {}),
        createdAt: now,
        updatedAt: now,
      }
      records.unshift(record)
      return Promise.resolve({
        contractId: id,
        number: record.number,
        status: 'DRAFT' as const,
        clientId: command.clientId,
        season: command.season,
        measurementMode: record.measurementMode,
      })
    },
    updateTerms(command) {
      const record = records.find((item) => item.id === command.contractId)
      if (!record) return Promise.reject(new Error('CONTRACT_NOT_FOUND'))
      const terms = command as unknown as Partial<NonNullable<ContractRecord['terms']>> & {
        priceCents?: number
      }
      record.terms = {
        services: terms.services ?? record.terms?.services ?? [],
        ...record.terms,
        ...Object.fromEntries(
          // Le type annonce des valeurs définies, mais la commande vient d'un
          // appelant : une valeur explicitement `undefined` écraserait la donnée
          // enregistrée. Le passage par `unknown` garde le contrôle d'exécution.
          Object.entries(terms).filter(
            ([key, value]) =>
              (value as unknown) !== undefined &&
              !['contractId', 'idempotencyKey', 'priceCents'].includes(key),
          ),
        ),
      }
      if (terms.priceCents !== undefined) record.priceCents = terms.priceCents
      record.updatedAt = new Date().toISOString()
      return Promise.resolve()
    },
    listClientProperties() {
      return Promise.resolve([])
    },
    listServiceLocations(contractId, options) {
      return Promise.resolve(options?.removed ? [] : (draftLocations.get(contractId) ?? []))
    },
    configureLocations(command) {
      const record = records.find((item) => item.id === command.contractId)
      if (!record) return Promise.reject(new Error('CONTRACT_NOT_FOUND'))
      record.submissionType = command.submissionType
      record.billingAddress = command.billingAddress
      draftLocations.set(
        command.contractId,
        command.locations.map((location, index) => ({
          id: `${command.contractId}-${String(index + 1)}`,
          contractId: command.contractId,
          address: location.address,
          formattedAddress:
            location.formattedAddress ??
            [
              location.address.line1,
              location.address.city,
              location.address.province,
              location.address.postalCode,
            ]
              .filter(Boolean)
              .join(', '),
          services: location.services ?? [],
          ...(location.instructions ? { instructions: location.instructions } : {}),
          measurementMode: location.measurementMode,
          ...(location.standardDrivewayCapacity === undefined
            ? {}
            : { standardDrivewayCapacity: location.standardDrivewayCapacity }),
          ...(location.priceCents === undefined ? {} : { priceCents: location.priceCents }),
          order: index + 1,
        })),
      )
      // Le total du Contrat est la somme des adresses couvertes, exactement
      // comme le recalcule `configure_contract_locations_v1`.
      const priced = command.locations.map((location) => location.priceCents)
      if (priced.length > 0 && priced.every((value) => value !== undefined)) {
        record.priceCents = priced.reduce<number>((sum, value) => sum + value, 0)
      }
      record.updatedAt = new Date().toISOString()
      return Promise.resolve()
    },
    listActiveTaxRatesPpm() {
      // Aucun taux n'est présumé hors Supabase : le document n'imprime alors
      // aucun détail de taxes.
      return Promise.resolve([])
    },
    listSchedule(contractId) {
      // Aucun échéancier n'est simulé; seul celui que l'opérateur vient de
      // composer dans l'Assistant est rendu.
      return Promise.resolve(draftSchedules.get(contractId) ?? [])
    },
    replaceSchedule(command) {
      const record = records.find((item) => item.id === command.contractId)
      if (!record) return Promise.reject(new Error('CONTRACT_NOT_FOUND'))
      const total = record.priceCents ?? 0
      draftSchedules.set(
        command.contractId,
        command.entries.map((entry, index) => ({
          id: `${command.contractId}-e${String(index + 1)}`,
          description: entry.description,
          kind: entry.kind,
          value: entry.value,
          amountCents: entry.kind === 'PERCENTAGE' ? Math.round((total * entry.value) / 100) : entry.value,
          ...(entry.dueOn ? { dueOn: entry.dueOn } : {}),
          order: index + 1,
        })),
      )
      const entries = draftSchedules.get(command.contractId) ?? []
      return Promise.resolve({
        contractId: command.contractId,
        entries: entries.length,
        scheduledCents: entries.reduce((sum, entry) => sum + entry.amountCents, 0),
        totalCents: total,
      })
    },
    listSignatures() {
      return Promise.resolve([])
    },
    listEvents() {
      return Promise.resolve([])
    },
    listNotes() {
      return Promise.resolve([])
    },
    createNote() {
      return Promise.reject(new Error('SUPABASE_REQUIRED'))
    },
    updateNote() {
      return Promise.reject(new Error('SUPABASE_REQUIRED'))
    },
    softDeleteNote() {
      return Promise.reject(new Error('SUPABASE_REQUIRED'))
    },
    recordSignature() {
      return Promise.reject(new Error('CONTRACT_SIGNATURE_SUPABASE_REQUIRED'))
    },
    recordManualSignature() {
      return Promise.reject(new Error('CONTRACT_MANUAL_SIGNATURE_SUPABASE_REQUIRED'))
    },
    activateNoInvoice() {
      return Promise.reject(new Error('CONTRACT_NO_INVOICE_SUPABASE_REQUIRED'))
    },
    changeStatus() {
      return Promise.reject(new Error('CONTRACT_STATUS_SUPABASE_REQUIRED'))
    },
    regularize() {
      return Promise.reject(new Error('CONTRACT_REGULARIZE_SUPABASE_REQUIRED'))
    },
    markNotInterested() {
      return Promise.reject(new Error('CONTRACT_NOT_INTERESTED_SUPABASE_REQUIRED'))
    },
    revertToSignature() {
      return Promise.reject(new Error('CONTRACT_REVERT_SIGNATURE_SUPABASE_REQUIRED'))
    },
    listAmendments() {
      return Promise.resolve([])
    },
    createAmendment() {
      return Promise.reject(new Error('CONTRACT_AMENDMENT_SUPABASE_REQUIRED'))
    },
    applyAmendment() {
      return Promise.reject(new Error('CONTRACT_AMENDMENT_SUPABASE_REQUIRED'))
    },
    archiveDocument() {
      return Promise.reject(new Error('CONTRACT_DOCUMENT_SUPABASE_REQUIRED'))
    },
    createCaptureUrl() {
      return Promise.reject(new Error('CONTRACT_CAPTURE_SUPABASE_REQUIRED'))
    },
    sendDocument() {
      // Un document contractuel fictif adressé à un vrai Client serait une
      // faute : l'envoi n'a pas de repli.
      return Promise.reject(new Error('CONTRACT_SEND_SUPABASE_REQUIRED'))
    },
    generateInvoices() {
      return Promise.reject(new Error('CONTRACT_INVOICES_SUPABASE_REQUIRED'))
    },
    listPendingClient() {
      // Aucun Contrat en attente n'est simulé : la liste Soumissions dépend de
      // `pending_contracts`, propre à Supabase.
      return Promise.resolve({
        items: [],
        meta: { source, retrievedAt: new Date().toISOString(), anomalies: 0 },
      })
    },
    scheduleFollowUp() {
      return Promise.reject(new Error('CONTRACT_FOLLOW_UP_SUPABASE_REQUIRED'))
    },
    softDelete() {
      return Promise.reject(new Error('CONTRACT_DELETE_SUPABASE_REQUIRED'))
    },
    restore() {
      return Promise.reject(new Error('CONTRACT_RESTORE_SUPABASE_REQUIRED'))
    },
    listDeleted(filters) {
      // Aucun Contrat supprimé n'est simulé en fixtures.
      return Promise.resolve({
        items: [],
        page: filters.page,
        pageSize: filters.pageSize,
        total: 0,
        meta: { source, retrievedAt: new Date().toISOString(), anomalies: 0 },
      })
    },
    geocodeAddress() {
      return Promise.reject(new Error('CONTRACT_GEOCODE_SUPABASE_REQUIRED'))
    },
    getBillingState() {
      return Promise.reject(new Error('CONTRACT_BILLING_SUPABASE_REQUIRED'))
    },
    reviseBilling() {
      return Promise.reject(new Error('CONTRACT_BILLING_SUPABASE_REQUIRED'))
    },
    updateAddress() {
      return Promise.reject(new Error('CONTRACT_ADDRESS_SUPABASE_REQUIRED'))
    },
    setMarkersRequired() {
      return Promise.reject(new Error('CONTRACT_MARKERS_SUPABASE_REQUIRED'))
    },
    setOverviewHidden() {
      return Promise.reject(new Error('CONTRACT_OVERVIEW_HIDDEN_SUPABASE_REQUIRED'))
    },
    setServiceLocationStatus() {
      return Promise.reject(new Error('SERVICE_LOCATION_STATUS_SUPABASE_REQUIRED'))
    },
  }
}
